import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router-dom";
import cx from "classnames";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faArrowRightLong,
} from "@fortawesome/pro-regular-svg-icons";

import myDashboardApi from "../../../../api/dashboard/myDashboardApi";
import AppRoutes from "../../../../routes/AppRoutes";
import DashboardCatchUpDto from "../../../../types/dtos/dashboards/right-bar/catch-ups/DashboardCatchUpDto";
import { Badge } from "../../../common";
import ManageCatchUpPopup from "../../../catch-ups/ManageCatchUpPopup";
import { LoadingPlaceholderItem } from "../shared/LoadingPlaceholderItem";
import CatchUpEmptyState from "./CatchUpEmptyState";
import CatchUpListItem from "./CatchUpListItem";

interface DashboardCatchUpsProps {
  initialCatchUps: DashboardCatchUpDto[];
}

const MAX_ITEMS_TO_DISPLAY = 4;

const DashboardCatchUps = ({ initialCatchUps }: DashboardCatchUpsProps) => {
  const auth = useAuth();
  const dashboardApi = new myDashboardApi(auth.user?.access_token);

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [catchUps, setCatchUps] =
    useState<DashboardCatchUpDto[]>(initialCatchUps);
  const [displayCatchUps, setDisplayCatchUps] = useState<DashboardCatchUpDto[]>(
    initialCatchUps.slice(0, MAX_ITEMS_TO_DISPLAY),
  );
  const [isAddCatchUpModalOpen, setIsAddCatchUpModalOpen] = useState(false);

  const showSkeleton = isLoading && displayCatchUps.length === 0;
  const isListBusy = isLoading || isRefreshing;

  const loadCatchUps = () => {
    const hasVisibleContent = displayCatchUps.length > 0;
    if (hasVisibleContent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    dashboardApi.getDashboardCatchUps(
      (data: DashboardCatchUpDto[]) => {
        setIsLoading(false);
        setIsRefreshing(false);
        setCatchUps(data);
        setDisplayCatchUps(data.slice(0, MAX_ITEMS_TO_DISPLAY));
      },
      (error: Error) => {
        console.error(error.message);
        setIsLoading(false);
        setIsRefreshing(false);
      },
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row">
        <header className="text-lg font-semibold mt-0 mb-2 basis-9/12 min-w-0 flex items-center gap-2">
          <span className="truncate">
            {t("DashboardRightBar.YourCatchUps", "Your catch-ups")}
          </span>
          <Badge
            text={catchUps.length.toString()}
            backgroundColourClassName="accent-bg"
            textColourClassName="text-white"
            classNames="shrink-0"
          />
        </header>
        <div className="basis-3/12 text-right">
          <Link
            to={AppRoutes.yourJourney.catchUps}
            className="group inline-flex items-center hover:cursor-pointer rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          >
            <span className="text-sm group-hover:underline">
              {t("Common.ViewAll", "View all")}
            </span>
            <FontAwesomeIcon
              icon={faArrowRightLong}
              className="ml-1"
              size="xs"
              aria-hidden="true"
            />
          </Link>
        </div>
      </div>

      {showSkeleton && (
        <>
          <LoadingPlaceholderItem />
          <LoadingPlaceholderItem />
          <LoadingPlaceholderItem />
          <LoadingPlaceholderItem />
        </>
      )}

      {!showSkeleton && (
        <>
          <div
            id="dashboard-catch-up-list"
            aria-busy={isListBusy}
            className={cx(
              "transition-opacity duration-200 ease-out",
              isRefreshing && displayCatchUps.length > 0 &&
                "opacity-70 pointer-events-none",
            )}
          >
            {displayCatchUps.length === 0 && <CatchUpEmptyState />}
            {displayCatchUps.length > 0 && (
              <div className="flex flex-col gap-2">
                {displayCatchUps.map((catchUp) => (
                  <div
                    className="snap-start"
                    key={catchUp.answerSetGuidId}
                  >
                    <CatchUpListItem catchUp={catchUp} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <hr className="border-gray-200 mt-2 mb-3" />
          <div className="flex justify-center">
            <button
              type="button"
              className="inline-flex items-center primary-colour font-semibold text-sm bg-transparent border-0 hover:underline hover:cursor-pointer rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setIsAddCatchUpModalOpen(true)}
            >
              <FontAwesomeIcon icon={faCalendarPlus} aria-hidden="true" />
              <span className="ml-2">
                {t("DashboardRightBar.BookACatchUp", "Book a catch-up")}
              </span>
            </button>
          </div>
        </>
      )}

      <ManageCatchUpPopup
        isOpen={isAddCatchUpModalOpen}
        onOpenChange={setIsAddCatchUpModalOpen}
        refreshParentTaskList={loadCatchUps}
        usageScenario="DASHBOARD"
      />
    </div>
  );
};

export default DashboardCatchUps;
